import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Loader2,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import Card from '../../components/Card';
import {
  answerGenerateQuestion,
  getGenerateSession,
  prefillGenerateSession,
  renderGenerateSession,
  reviseGenerateSession,
  startGenerateSession,
} from './generateApi';
import {
  CompletionView,
} from './GenerateCompletion';
import { formatAnswerValue } from '../../utils/format';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';

// Quick-select chips for a question. Prefer the spec's explicit
// `chips` array when present — that's the canonical list. Fall back to
// scraping single-quoted values out of the `options` hint string for
// older specs (NDA pre-dates the explicit chips field).
//
// The fallback regex requires a word boundary on the opening quote so
// it doesn't pair the apostrophe inside prose like "users'" with the
// next quote and capture nonsense.
function parseOptionChips(options) {
  if (!options) return [];
  const chips = [];
  const re = /(?:^|[\s([])'([A-Za-z0-9_][A-Za-z0-9_\s\-/]*?)'(?=[\s,.)\]]|$)/g;
  let m;
  while ((m = re.exec(options)) !== null) chips.push(m[1].trim());
  return chips;
}

// Normalize chips to { value, label } objects regardless of how the
// question spec declared them. Strings get a value-as-label fallback,
// with a small tidy-up (snake_case → spaces).
function chipsForQuestion(question) {
  const raw = (Array.isArray(question.chips) && question.chips.length > 0)
    ? question.chips
    : parseOptionChips(question.options);
  return raw.map((c) =>
    typeof c === 'string'
      ? { value: c, label: prettifyValue(c) }
      : { value: c.value, label: c.label || prettifyValue(c.value) }
  );
}

function prettifyValue(s) {
  const t = String(s || '');
  if (!t) return t;
  return t.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function Kbd({ children }) {
  return (
    <kbd
      style={{
        fontSize: 10,
        padding: '1px 5px',
        borderRadius: 4,
        background: 'var(--surface-alpha-primary)',
        border: '0.5px solid var(--border-primary)',
        fontFamily: 'var(--font-family-mono)',
      }}
    >
      {children}
    </kbd>
  );
}

function macOrCtrl() {
  if (typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)) {
    return '⌘';
  }
  return 'Ctrl';
}

// Human note for questions an answer just pruned, grouped by section label
// ("Skipped Licensing (4 questions) — not applicable based on your answer").
export function skipNoteFor(skippedFields, sections) {
  if (!skippedFields?.length) return null;
  const bySection = new Map();
  for (const field of skippedFields) {
    const section = (sections || []).find((s) => (s.fields || []).includes(field));
    const label = section ? section.label : null;
    bySection.set(label, (bySection.get(label) || 0) + 1);
  }
  const parts = [...bySection.entries()].map(([label, n]) => {
    const count = `${n} question${n === 1 ? '' : 's'}`;
    return label ? `${label} (${count})` : count;
  });
  return `Skipped ${parts.join(', ')} — not applicable based on your answer.`;
}

// Stepper above the progress bar: applicable sections with done/active/
// upcoming states. Sections that no longer apply (every question skipped)
// drop out entirely; the skip note explains why.
function SectionStepper({ sections, currentField }) {
  const applicable = (sections || []).filter((s) => s.applicable);
  if (applicable.length < 2 || !currentField) return null;
  const activeIndex = applicable.findIndex((s) => (s.fields || []).includes(currentField));
  if (activeIndex === -1) return null;
  return (
    <div className="row" style={{ gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {applicable.map((s, i) => {
        const state = i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'upcoming';
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <span className="muted" style={{ fontSize: 10 }}>›</span>}
            <span
              style={{
                fontSize: 11,
                fontWeight: state === 'active' ? 600 : 400,
                color:
                  state === 'active' ? 'var(--color-purple-500)'
                  : state === 'done' ? 'var(--text-primary)'
                  : 'var(--text-tertiary)',
              }}
            >
              {state === 'done' ? '✓ ' : ''}{s.label.replace(/\s*\(.*\)$/, '')}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// All-at-once form view. The whole questionnaire is visible; the server's
// questionStates drive each row:
//   answered — locked value with an Edit (revise) affordance
//   active   — the editable frontier (a single question or a group cluster)
//   upcoming — visible but disabled; shows a ghost of any note-prefill
//   skipped  — grayed "not applicable" (pruned by a branch)
// Answers still submit one field at a time through the same validated flow —
// the form enforces the sequence by only enabling the frontier.
// ---------------------------------------------------------------------------

function AnsweredRow({ q, submitting, reviseTo }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
      <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-primary)', wordBreak: 'break-word', flex: 1 }}>
        {formatAnswerValue(q.value)}
      </span>
      <button
        type="button"
        className="btn ghost xs"
        onClick={() => reviseTo(q.questionIndex)}
        disabled={submitting}
        title="Jump back to this question and re-answer (later answers reset)"
      >
        Edit
      </button>
    </div>
  );
}

function UpcomingRow({ q, suggestion }) {
  return (
    <>
      <input
        className="input"
        value={suggestion || ''}
        placeholder="—"
        disabled
        readOnly
        style={{ width: '100%', opacity: 0.7 }}
      />
      {suggestion && (
        <div className="muted" style={{ fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Sparkles className="w-3 h-3" />
          From your notes — will pre-fill when this question unlocks.
        </div>
      )}
    </>
  );
}

function ActiveEditor({
  q, isGroup, isLastActive, clarification, suggestions,
  input, setInput, groupValues, setGroupValues,
  submitting, submit, submitGroup, inputRef, autoFocusRef, canSubmitGroup,
}) {
  const chips = chipsForQuestion(q);
  const value = isGroup ? (groupValues[q.field] || '') : input;
  const setValue = isGroup
    ? (v) => setGroupValues((cur) => ({ ...cur, [q.field]: v }))
    : setInput;
  const onEnter = isGroup ? submitGroup : () => submit();
  const wasSuggested = suggestions[q.field] && value === suggestions[q.field];

  return (
    <div>
      {clarification && (
        <div className="callout yellow" style={{ marginBottom: 10 }}>
          <div className="callout-body" style={{ fontSize: 12 }}>{clarification}</div>
        </div>
      )}
      {chips.length > 0 && !isGroup && (
        <div className="row" style={{ gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {chips.map((c) => (
            <button
              key={c.value}
              className="btn outline sm"
              onClick={() => submit(c.label)}
              disabled={submitting}
              type="button"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {q.examples?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <span className="muted" style={{ fontSize: 11, marginRight: 8 }}>Start from an example:</span>
          {q.examples.map((ex) => (
            <button
              key={ex.label}
              className="btn ghost xs"
              type="button"
              disabled={submitting}
              title="Insert this template into the answer box — edit before continuing"
              onClick={() => {
                setValue(value.trim() ? `${value.trim()}\n${ex.text}` : ex.text);
                inputRef.current?.focus();
              }}
              style={{ marginRight: 4 }}
            >
              {ex.label}
            </button>
          ))}
        </div>
      )}
      {q.multiline ? (
        <textarea
          ref={(el) => { if (autoFocusRef) { inputRef.current = el; } }}
          className="textarea"
          placeholder="Type your answer…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onEnter();
            }
          }}
          disabled={submitting}
          rows={4}
          style={{ width: '100%', height: 'auto', minHeight: 96, resize: 'vertical', padding: '10px 12px', lineHeight: 1.5 }}
        />
      ) : (
        <input
          ref={(el) => { if (autoFocusRef) { inputRef.current = el; } }}
          className="input"
          placeholder="Type your answer…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onEnter();
            }
          }}
          disabled={submitting}
          style={{ width: '100%' }}
        />
      )}
      {wasSuggested && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Sparkles className="w-3 h-3" />
          Pre-filled from your notes — review, edit if needed, then continue.
        </div>
      )}
      {isLastActive && (
        <div className="row" style={{ gap: 10, marginTop: 10, alignItems: 'center' }}>
          <button
            className="btn primary"
            disabled={submitting || (isGroup ? !canSubmitGroup : !input.trim())}
            onClick={() => (isGroup ? submitGroup() : submit())}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Next
          </button>
          <span className="muted" style={{ fontSize: 11 }}>
            {q.multiline
              ? <>Press <Kbd>{macOrCtrl()}</Kbd> + <Kbd>Enter</Kbd> to continue</>
              : <>Press <Kbd>Enter</Kbd> to continue</>}
          </span>
        </div>
      )}
    </div>
  );
}

// Active group cluster, rendered as ONE visually contained question. In the
// flat form view the group members looked like independent questions, so the
// single Next under the last member read as belonging to "the question after"
// — and a half-filled group left a silently disabled button. This block makes
// the containment explicit: shared heading, Enter hops between parts, and the
// button names whatever is still empty.
function ActiveGroupBlock({
  rows, clarification, suggestions,
  groupValues, setGroupValues, submitting, submitGroup, inputRef,
}) {
  const fieldRefs = useRef({});
  const missing = rows.filter((r) => !(groupValues[r.field] || '').trim());
  const anySuggested = rows.some(
    (r) => suggestions[r.field] && groupValues[r.field] === suggestions[r.field]
  );
  const groupLabel = rows[0]?.group?.label || rows[0]?.text;

  return (
    <div
      style={{
        background: 'var(--surface-alpha-primary)',
        border: '0.5px solid var(--border-primary)',
        borderRadius: 10,
        padding: '14px 16px',
        margin: '6px 0',
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.5 }}>
        {groupLabel}
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginBottom: 12 }}>
        One question, {rows.length} parts — Next saves them together.
      </div>
      {clarification && (
        <div className="callout yellow" style={{ marginBottom: 10 }}>
          <div className="callout-body" style={{ fontSize: 12 }}>{clarification}</div>
        </div>
      )}
      {rows.map((f, idx) => (
        <div key={f.field} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
            {f.shortLabel || f.text}
          </div>
          {f.options && (
            <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>{f.options}</div>
          )}
          <input
            ref={(el) => {
              fieldRefs.current[f.field] = el;
              if (idx === 0) inputRef.current = el;
            }}
            className="input"
            placeholder="Type your answer…"
            value={groupValues[f.field] || ''}
            onChange={(e) => setGroupValues((cur) => ({ ...cur, [f.field]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey) return;
              e.preventDefault();
              if (idx < rows.length - 1) {
                fieldRefs.current[rows[idx + 1].field]?.focus();
              } else {
                submitGroup();
              }
            }}
            disabled={submitting}
            style={{ width: '100%' }}
          />
        </div>
      ))}
      {anySuggested && (
        <div className="muted" style={{ fontSize: 11, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Sparkles className="w-3 h-3" />
          Some parts were pre-filled from your notes — review before continuing.
        </div>
      )}
      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
        <button
          className="btn primary"
          disabled={submitting || missing.length > 0}
          onClick={submitGroup}
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Next
        </button>
        <span className="muted" style={{ fontSize: 11 }}>
          {missing.length > 0
            ? `Fill in ${missing.map((r) => r.shortLabel || r.field).join(' and ')} to continue`
            : <>Press <Kbd>Enter</Kbd> to continue</>}
        </span>
      </div>
    </div>
  );
}

function FormQuestions({
  questionStates, sections, suggestions, clarification,
  input, setInput, groupValues, setGroupValues,
  submitting, submit, submitGroup, reviseTo, inputRef,
}) {
  const activeRows = questionStates.filter((q) => q.status === 'active');
  const isGroup = activeRows.length > 1;
  const firstActiveField = activeRows.length ? activeRows[0].field : null;
  const activeRowRef = useRef(null);

  // Keep the frontier in view as it advances down the form.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [firstActiveField]);

  // Bucket questions by section (specs without sections render one flat list).
  const buckets = [];
  if (sections?.length) {
    const fieldToBucket = new Map();
    for (const s of sections) {
      const bucket = { id: s.id, label: s.label, rows: [] };
      buckets.push(bucket);
      for (const f of s.fields || []) fieldToBucket.set(f, bucket);
    }
    const orphan = { id: '_other', label: null, rows: [] };
    for (const q of questionStates) {
      (fieldToBucket.get(q.field) || orphan).rows.push(q);
    }
    if (orphan.rows.length) buckets.push(orphan);
  } else {
    buckets.push({ id: '_all', label: null, rows: questionStates });
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {buckets.filter((b) => b.rows.length > 0).map((bucket) => {
        const allSkipped = bucket.rows.every((q) => q.status === 'skipped');
        return (
          <Card key={bucket.id} flush style={allSkipped ? { opacity: 0.5 } : undefined}>
            <div style={{ padding: '16px 20px' }}>
              {bucket.label && (
                <div
                  className="row"
                  style={{
                    gap: 8, alignItems: 'center', marginBottom: 12,
                    fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                    letterSpacing: 0.6, color: 'var(--text-secondary)',
                  }}
                >
                  {bucket.label.replace(/\s*\(.*\)$/, '')}
                  {allSkipped && <span className="chip gray sq">Not applicable</span>}
                </div>
              )}
              {(() => {
                // Collapse the active group's consecutive rows into a single
                // contained block; everything else renders as ordinary rows.
                const activeFieldSet = new Set(activeRows.map((r) => r.field));
                const segments = [];
                for (const q of bucket.rows) {
                  const prev = segments[segments.length - 1];
                  if (isGroup && q.status === 'active' && activeFieldSet.has(q.field)) {
                    if (prev?.kind === 'group') prev.rows.push(q);
                    else segments.push({ kind: 'group', rows: [q] });
                  } else {
                    segments.push({ kind: 'row', q });
                  }
                }
                return segments.map((segment, i) => {
                  if (segment.kind === 'group') {
                    return (
                      <div
                        key={`group-${segment.rows[0].field}`}
                        ref={segment.rows.some((r) => r.field === firstActiveField) ? activeRowRef : undefined}
                        style={{ padding: '6px 0', borderTop: i > 0 ? '0.5px solid var(--border-primary)' : 0 }}
                      >
                        <ActiveGroupBlock
                          rows={segment.rows}
                          clarification={segment.rows.some((r) => r.field === firstActiveField) ? clarification : null}
                          suggestions={suggestions}
                          groupValues={groupValues}
                          setGroupValues={setGroupValues}
                          submitting={submitting}
                          submitGroup={submitGroup}
                          inputRef={inputRef}
                        />
                      </div>
                    );
                  }
                  const { q } = segment;
                  const isActive = q.status === 'active';
                  const dimmed = q.status === 'skipped';
                  return (
                  <div
                    key={q.field}
                    ref={isActive && q.field === firstActiveField ? activeRowRef : undefined}
                    style={{
                      padding: '12px 0',
                      borderTop: i > 0 ? '0.5px solid var(--border-primary)' : 0,
                      opacity: dimmed ? 0.45 : q.status === 'upcoming' ? 0.75 : 1,
                    }}
                  >
                    <div className="row" style={{ gap: 8, alignItems: 'baseline', marginBottom: q.status === 'answered' ? 4 : 6 }}>
                      <div
                        style={{
                          fontSize: isActive ? 13.5 : 12.5,
                          fontWeight: isActive ? 600 : 500,
                          color: 'var(--text-primary)',
                          lineHeight: 1.5,
                          flex: 1,
                        }}
                      >
                        {q.shortLabel || q.text}
                      </div>
                      {dimmed && <span className="muted" style={{ fontSize: 11, flexShrink: 0 }}>Not applicable</span>}
                    </div>
                    {isActive && q.options && (
                      <div className="muted" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>{q.options}</div>
                    )}
                    {q.status === 'answered' && (
                      <AnsweredRow q={q} submitting={submitting} reviseTo={reviseTo} />
                    )}
                    {q.status === 'upcoming' && (
                      <UpcomingRow q={q} suggestion={suggestions[q.field]} />
                    )}
                    {isActive && (
                      <ActiveEditor
                        q={q}
                        isGroup={false}
                        isLastActive
                        clarification={q.field === firstActiveField ? clarification : null}
                        suggestions={suggestions}
                        input={input}
                        setInput={setInput}
                        groupValues={groupValues}
                        setGroupValues={setGroupValues}
                        submitting={submitting}
                        submit={submit}
                        submitGroup={submitGroup}
                        inputRef={inputRef}
                        autoFocusRef={q.field === firstActiveField}
                        canSubmitGroup={false}
                      />
                    )}
                  </div>
                  );
                });
              })()}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function Wizard({ typeKey, sessionIdFromUrl, typeMeta, onDone, onRestart }) {
  // Local state for the in-flight flow. Server holds the authoritative
  // answers (in screening_sessions); we keep a shadow copy here for the
  // "Your answers" sidebar and pre-fill on resume.
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(sessionIdFromUrl || null);
  const [question, setQuestion] = useState(null);
  const [history, setHistory] = useState([]); // [{ field, value, question }]
  const [input, setInput] = useState('');
  const [clarification, setClarification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [questionCount, setQuestionCount] = useState(null);
  const [isBundle, setIsBundle] = useState(false);
  const [isScreening, setIsScreening] = useState(false);
  const [bundleDocs, setBundleDocs] = useState(null);
  // Values for grouped-question screens, keyed by field. Field names are
  // unique per spec, so stale keys from earlier screens are harmless.
  const [groupValues, setGroupValues] = useState({});
  const [sections, setSections] = useState(null);
  const [questionStates, setQuestionStates] = useState(null);
  const [skipNote, setSkipNote] = useState(null);
  // Early-verdict offer: set when the server reports the screening outcome
  // is already determined by the answers so far. Dismissable; recomputed on
  // every answer.
  const [pendingOutcome, setPendingOutcome] = useState(null);
  const [pendingDismissed, setPendingDismissed] = useState(false);
  // AI prefill-from-notes: suggestions keyed by field, applied as the
  // matching question comes up (the user still confirms each one).
  const [suggestions, setSuggestions] = useState({});
  const [prefillNotes, setPrefillNotes] = useState('');
  const [prefillOpen, setPrefillOpen] = useState(false);
  const [prefillBusy, setPrefillBusy] = useState(false);
  const [prefillSummary, setPrefillSummary] = useState(null);
  const inputRef = useRef(null);

  // Answers persist server-side as they're submitted and the session is
  // resumable from its URL — the only thing at risk on navigation is the
  // answer being typed right now (or pre-filled and not yet confirmed).
  const currentGroupFields = question?.groupPeers?.length > 0
    ? [question, ...question.groupPeers]
    : [];
  const hasUnsubmittedInput =
    !result &&
    !rendering &&
    (input.trim() !== '' ||
      currentGroupFields.some((f) => String(groupValues[f.field] || '').trim() !== ''));
  useUnsavedChangesGuard(
    hasUnsubmittedInput,
    "Your current answer hasn't been submitted yet. Leave and discard it? Answers you already submitted are saved."
  );

  // Apply a /start- or /sessions/:id-shaped server response to local state.
  function applySessionResponse(body) {
    setSessionId(body.sessionId || body.flowId);
    setQuestion(body.nextQuestion);
    setQuestionCount(body.questionCount || null);
    setIsBundle(!!body.bundle);
    setIsScreening(!!body.screening);
    setBundleDocs(body.documents || null);
    setSections(body.sections || null);
    setQuestionStates(body.questionStates || null);
    setHistory(body.history || []);
    // Session reloads/revises invalidate any standing early-verdict offer —
    // it only rides on answer responses and is recomputed there.
    setPendingOutcome(null);
    setPendingDismissed(false);
    if (body.outcome && body.status === 'completed') {
      // Already rendered: show the completion view directly.
      setResult({ outcome: body.outcome, documents: [] });
    }
  }

  // Bootstrap: either resume an existing session (sessionIdFromUrl present)
  // or start a fresh one. The URL is the source of truth — if it changes,
  // we re-fetch.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setResult(null);
    const request = sessionIdFromUrl
      ? getGenerateSession(sessionIdFromUrl)
      : startGenerateSession(typeKey);
    request
      .then((body) => {
        if (cancelled) return;
        applySessionResponse(body);
        // Stamp the new session id into the URL so reload/share works.
        if (!sessionIdFromUrl && body.sessionId) {
          navigate(`/intake/${typeKey}/${body.sessionId}`, { replace: true });
        }
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, sessionIdFromUrl]);

  // Autofocus the input on every new question.
  useEffect(() => {
    if (question) inputRef.current?.focus();
  }, [question]);

  // Apply note suggestions as their question comes up — pre-fill only into
  // empty inputs so we never clobber something the user typed.
  useEffect(() => {
    if (!question) return;
    if (question.groupPeers?.length > 0) {
      const fields = [question, ...question.groupPeers];
      setGroupValues((v) => {
        const next = { ...v };
        for (const f of fields) {
          if (suggestions[f.field] && !next[f.field]) next[f.field] = suggestions[f.field];
        }
        return next;
      });
    } else if (suggestions[question.field]) {
      setInput((cur) => cur || suggestions[question.field]);
    }
  }, [question, suggestions]);

  // Core prefill against an explicit session + notes. Kept parameterized so it
  // can run both from the in-wizard panel and auto-triggered from notes the
  // launchpad passed via router state (where `prefillNotes` state isn't set yet).
  const doPrefill = useCallback(async (sid, notes) => {
    if (!sid || !notes.trim()) return;
    setPrefillBusy(true);
    setError(null);
    try {
      const body = await prefillGenerateSession(sid, notes);
      setSuggestions(body.suggestions || {});
      setPrefillSummary(
        body.count > 0
          ? `Found ${body.count} answer${body.count === 1 ? '' : 's'} in your notes — they'll appear pre-filled as you go. Review each one before continuing.`
          : 'No clear answers found in those notes — you can edit them and try again.'
      );
      if (body.count > 0) setPrefillOpen(false);
    } catch (err) {
      setError(err?.message || 'Prefill failed');
    } finally {
      setPrefillBusy(false);
    }
  }, []);

  const runPrefill = useCallback(() => {
    if (prefillBusy) return;
    return doPrefill(sessionId, prefillNotes);
  }, [doPrefill, sessionId, prefillBusy, prefillNotes]);

  // Notes the launchpad collected before the session existed ride in on router
  // state. Once the session is loaded, seed the panel and run prefill once, then
  // strip the state so a reload doesn't replay it.
  const autoPrefilledRef = useRef(false);
  useEffect(() => {
    const notes = location.state?.prefillNotes;
    if (!notes || !sessionId || autoPrefilledRef.current) return;
    autoPrefilledRef.current = true;
    setPrefillNotes(notes);
    setPrefillOpen(true);
    doPrefill(sessionId, notes);
    navigate(location.pathname, { replace: true, state: {} });
  }, [sessionId, location.state, location.pathname, doPrefill, navigate]);

  // `overrideValue` lets chips submit in one click without round-tripping
  // through the input state. Keyboard/button paths call submit() with no args.
  const pushHistoryEntry = useCallback((body, fallbackText) => {
    setHistory((h) => [
      ...h,
      {
        field: body.capturedField,
        value: body.capturedValue,
        question: body.capturedQuestion || fallbackText,
        questionIndex: body.capturedIndex,
      },
    ]);
  }, []);

  // Progress metadata rides on every answer response: the exact remaining
  // plan (it shrinks as branches resolve), section applicability, and any
  // questions this answer just pruned (surfaced as a skip note).
  const applyAnswerMeta = useCallback((body) => {
    if (body.questionCount) setQuestionCount(body.questionCount);
    if (body.sections) setSections(body.sections);
    if (body.questionStates) setQuestionStates(body.questionStates);
    setSkipNote(skipNoteFor(body.newlySkippedFields, body.sections));
    setPendingOutcome(body.pendingOutcome || null);
  }, []);

  // Auto-render — no intermediate "ready to render" screen. If legal wants
  // a confirm step later, we add one here.
  const completeAndRender = useCallback(async ({ concludeEarly = false } = {}) => {
    setQuestion(null);
    setRendering(true);
    try {
      const b2 = await renderGenerateSession(sessionId, { concludeEarly });
      setResult(b2);
      onDone?.(b2);
    } catch (err) {
      setError(err?.message || 'Failed to render document');
    } finally {
      setRendering(false);
    }
  }, [sessionId, onDone]);

  const submit = useCallback(async (overrideValue) => {
    if (!sessionId || !question || submitting) return;
    const response = (typeof overrideValue === 'string' ? overrideValue : input).trim();
    if (!response) return;
    setSubmitting(true);
    setClarification(null);
    setError(null);
    try {
      const body = await answerGenerateQuestion(sessionId, response);
      if (body.status === 'needs_clarification') {
        setClarification(body.clarification || 'Could you clarify that?');
        return;
      }
      pushHistoryEntry(body, question.text);
      applyAnswerMeta(body);
      setInput('');
      if (body.status === 'advanced') {
        setQuestion(body.nextQuestion);
      } else if (body.status === 'completed') {
        await completeAndRender();
      }
    } catch (err) {
      setError(err?.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, question, input, submitting, pushHistoryEntry, applyAnswerMeta, completeAndRender]);

  // Grouped screens submit one field at a time under the hood — the flow,
  // extraction, and Slack/CLI clients all stay per-field. A clarification
  // mid-group stops the run and resyncs from the server, which still points
  // at the failing field (with the remaining peers), so the card re-renders
  // from exactly where validation stopped; entered values are kept.
  const submitGroup = useCallback(async () => {
    if (!sessionId || !question || submitting) return;
    const fields = [question, ...(question.groupPeers || [])];
    const values = fields.map((f) => (groupValues[f.field] || '').trim());
    if (values.some((v) => !v)) return;
    setSubmitting(true);
    setClarification(null);
    setError(null);
    try {
      for (let i = 0; i < fields.length; i++) {
        const body = await answerGenerateQuestion(sessionId, values[i]);
        if (body.status === 'needs_clarification') {
          setClarification(body.clarification || 'Could you clarify that?');
          const sess = await getGenerateSession(sessionId);
          applySessionResponse(sess);
          return;
        }
        pushHistoryEntry(body, fields[i].text);
        applyAnswerMeta(body);
        if (body.status === 'completed') {
          await completeAndRender();
          return;
        }
        if (i === fields.length - 1) {
          setQuestion(body.nextQuestion);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to submit answers');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, question, submitting, groupValues, pushHistoryEntry, applyAnswerMeta, completeAndRender]);

  // Jump back to a previous question. Calls the server's revise endpoint
  // so any drafts already rendered for this session get cleared, then
  // rehydrates local state with the new (truncated) history.
  const reviseTo = useCallback(async (questionIndex) => {
    if (!sessionId) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const body = await reviseGenerateSession(sessionId, questionIndex);
      applySessionResponse(body);
      setInput('');
    } catch (err) {
      setError(err?.message || 'Failed to revise');
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Progress. The server recomputes the skip-aware total on every response.
  const total = questionCount;
  const step = history.length + (question ? 1 : 0);
  const showScreeningIntro = isScreening && history.length === 0;
  const showBundleIntro = isBundle && !isScreening && history.length === 0 && bundleDocs?.length;

  // Completed state — render result view.
  if (result) {
    return (
      <CompletionView result={result} typeMeta={typeMeta} onRestart={onRestart} />
    );
  }

  return (
    <div className="canvas-inner animate-fade-in" style={{ maxWidth: 760 }}>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Intake · {typeMeta?.displayName || typeKey}</div>
          <h1 className="page-title">
            {rendering ? 'Generating your draft…' : question ? `Step ${step}${total ? ` of ${total}` : ''}` : 'Preparing…'}
          </h1>
        </div>
        <button className="btn ghost sm" onClick={onRestart} title="Start over">
          <RotateCcw className="w-3.5 h-3.5" />
          Start over
        </button>
      </div>

      {/* Section stepper + progress bar */}
      {!rendering && <SectionStepper sections={sections} currentField={question?.field} />}
      {total && (
        <div
          className="fc-meter"
          style={{
            height: 4, background: 'var(--color-gray-100)',
            borderRadius: 999, overflow: 'hidden', marginBottom: 18,
          }}
        >
          <div
            className="fc-meter"
            style={{
              width: `${Math.min(100, (step / total) * 100)}%`,
              height: '100%', background: 'var(--color-purple-500)',
              transition: 'width 200ms ease',
            }}
          />
        </div>
      )}

      {skipNote && question && !rendering && (
        <div className="callout" style={{ marginBottom: 14 }}>
          <div className="callout-body muted" style={{ fontSize: 12 }}>{skipNote}</div>
        </div>
      )}

      {pendingOutcome && !pendingDismissed && question && !rendering && (
        <div
          className={`callout ${pendingOutcome.severity === 'critical' ? 'red' : 'yellow'}`}
          style={{ marginBottom: 14 }}
        >
          <div className="callout-body" style={{ fontSize: 12 }}>
            <strong>The screening verdict is already determined:</strong> {pendingOutcome.summary}.
            <div className="muted" style={{ marginTop: 4 }}>
              The remaining questions only enrich the record for the handoff — they can't change
              the outcome.
            </div>
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              <button
                className="btn primary sm"
                type="button"
                disabled={submitting}
                onClick={() => completeAndRender({ concludeEarly: true })}
              >
                End now &amp; see outcome
              </button>
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => setPendingDismissed(true)}
              >
                Keep answering
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="callout red" style={{ marginBottom: 14 }}>
          <div className="callout-body">
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>{error}</div>
          </div>
        </div>
      )}

      {rendering && (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '0 auto 12px', color: 'var(--color-purple-500)' }} />
          <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>
            {isScreening
              ? 'Evaluating your answers and assembling the right output…'
              : isBundle
              ? `Rendering ${bundleDocs?.length || 'all'} documents and creating your drafts…`
              : 'Rendering the document and creating your draft…'}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>This only takes a few seconds.</div>
        </div>
      )}

      {showScreeningIntro && !rendering && (
        <div className="callout purple" style={{ marginBottom: 14 }}>
          <div className="callout-body" style={{ fontSize: 12 }}>
            <strong>Intake.</strong> The full questionnaire is below — answer in order;
            questions unlock as you go, and branches that don't apply gray out. Depending on
            the answers, the wizard will either generate the right contract package or surface
            an escalation path.
          </div>
        </div>
      )}

      {/* Prefill is available for every intake type, not just screenings. */}
      {question && !rendering && !prefillOpen && !prefillSummary && (
        <div style={{ marginBottom: 14 }}>
          <button className="btn outline xs" type="button" onClick={() => setPrefillOpen(true)}>
            <Sparkles className="w-3 h-3" />
            Prefill from notes
          </button>
        </div>
      )}

      {prefillOpen && question && !rendering && (
        <Card flush style={{ marginBottom: 14 }}>
          <div style={{ padding: '16px 18px', marginBottom: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
              Prefill from notes
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
              Paste call notes, an email thread, or a CRM summary. We'll extract whatever
              questionnaire answers are clearly present and pre-fill them — you confirm each
              one as you go.
            </div>
            <textarea
              className="textarea"
              rows={5}
              placeholder="Paste notes here…"
              value={prefillNotes}
              onChange={(e) => setPrefillNotes(e.target.value)}
              disabled={prefillBusy}
              style={{ width: '100%', minHeight: 110, resize: 'vertical', padding: '10px 12px', lineHeight: 1.5, marginBottom: 10 }}
            />
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn ghost sm" type="button" disabled={prefillBusy} onClick={() => setPrefillOpen(false)}>
                Cancel
              </button>
              <button className="btn primary sm" type="button" disabled={prefillBusy || !prefillNotes.trim()} onClick={runPrefill}>
                {prefillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Extract answers
              </button>
            </div>
          </div>
        </Card>
      )}

      {prefillSummary && question && !rendering && (
        <div className="callout" style={{ marginBottom: 14, marginTop: prefillOpen ? 14 : 0 }}>
          <div className="callout-body muted" style={{ fontSize: 12 }}>
            {prefillSummary}
            {!prefillOpen && (
              <button
                className="btn ghost xs"
                type="button"
                style={{ marginLeft: 8 }}
                onClick={() => setPrefillOpen(true)}
              >
                Edit notes
              </button>
            )}
          </div>
        </div>
      )}

      {showBundleIntro && !rendering && (
        <div className="callout purple" style={{ marginBottom: 14 }}>
          <div className="callout-body" style={{ fontSize: 12 }}>
            <strong>Heads up:</strong> your answers will generate all {bundleDocs.length} documents in this suite —{' '}
            {bundleDocs.join(', ')}. You'll only answer the shared questions once.
          </div>
        </div>
      )}

      {questionStates && question && !rendering && (
        <FormQuestions
          questionStates={questionStates}
          sections={sections}
          suggestions={suggestions}
          clarification={clarification}
          input={input}
          setInput={setInput}
          groupValues={groupValues}
          setGroupValues={setGroupValues}
          submitting={submitting}
          submit={submit}
          submitGroup={submitGroup}
          reviseTo={reviseTo}
          inputRef={inputRef}
        />
      )}
    </div>
  );
}

export default Wizard;
